import { useParams, Navigate, Link } from "react-router-dom";
import { MapPin, ArrowRight } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { WhatsAppButton, CallButton } from "@/components/CTAButtons";
import Differentials from "@/components/sections/Differentials";
import Testimonials from "@/components/sections/Testimonials";
import FaqSection from "@/components/sections/FaqSection";
import { FILIAIS, ALL_UNITS } from "@/data/filiais";
import { CONTACT, SERVICES } from "@/data/site";
import { serviceSchema, breadcrumbSchema } from "@/lib/schema";

export default function FilialPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const f = FILIAIS[slug];

  if (!f) return <Navigate to="/" replace />;

  const label = f.kind === "Matriz" ? "Matriz" : "Filial";
  const others = ALL_UNITS.filter((u) => u.slug !== slug);

  return (
    <>
      <SEO
        title={f.seoTitle}
        description={f.seoDescription}
        path={f.path}
        keywords={f.keywords.join(", ")}
        schemas={[
          serviceSchema(`Guincho e auto socorro em ${f.city} - ${f.uf}`, f.seoDescription),
          breadcrumbSchema([{ name: "Início", path: "/" }, { name: `${label} ${f.city}`, path: f.path }]),
        ]}
      />

      <PageHeader
        eyebrow={`${label} · ${f.city} — ${f.uf}`}
        title={<>Guincho em<br /><span className="text-brand-red">{f.city}</span></>}
        subtitle={f.intro}
        image={f.image}
        trail={[{ name: "Início", path: "/" }, { name: `${label} ${f.city}`, path: f.path }]}
      />

      <section className="bg-brand-obsidian py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_320px] lg:gap-16">
            <Reveal>
              <div className="space-y-4 text-sm leading-relaxed text-white/65 md:text-base">
                {f.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </Reveal>

            <aside className="space-y-6">
              <Reveal className="rounded-sm border border-white/10 bg-brand-coal p-6">
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wide text-white">Atendimento imediato</h3>
                <p className="mt-2 text-sm text-white/55">Solicite seu guincho em {f.city} agora, 24 horas por dia.</p>
                <div className="mt-5 flex flex-col gap-3">
                  <WhatsAppButton label="WhatsApp" message={`Olá! Preciso de um guincho em ${f.city} (${f.uf}). Podem me atender?`} testid="filial-whatsapp" size="sm" className="w-full" />
                  <CallButton testid="filial-call" size="sm" className="w-full" />
                </div>
                <p className="mt-4 text-center font-mono text-xs text-white/40">{CONTACT.phoneDisplay}</p>
              </Reveal>

              <Reveal className="rounded-sm border border-white/10 bg-brand-coal p-6" delay={0.1}>
                <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-brand-red">Também atendemos</h3>
                <ul className="mt-4 space-y-2">
                  {f.also.map((c) => (
                    <li key={c} className="flex items-center gap-2 text-sm text-white/70">
                      <MapPin weight="fill" className="h-4 w-4 shrink-0 text-brand-red" /> {c}
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal className="rounded-sm border border-white/10 bg-brand-coal p-6" delay={0.15}>
                <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-brand-red">Serviços</h3>
                <ul className="mt-4 space-y-2">
                  {SERVICES.map((s) => (
                    <li key={s.slug}>
                      <Link to={`/servicos#${s.slug}`} className="text-sm text-white/60 transition-colors hover:text-white">{s.title}</Link>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </aside>
          </div>

          {/* Other units */}
          <div className="mt-16 border-t border-white/10 pt-12">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-red">Outras unidades</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={o.path}
                  data-testid={`other-unit-${o.slug}`}
                  className="group flex items-center justify-between rounded-sm border border-white/10 bg-brand-coal p-5 transition-colors duration-300 hover:border-brand-red"
                >
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-brand-red">{o.kind} · {o.uf}</p>
                    <p className="font-heading text-xl font-semibold uppercase tracking-wide text-white">{o.city}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-white/40 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand-red" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Differentials />
      <Testimonials />
      <FaqSection />
    </>
  );
}
